import { useLayoutEffect, useMemo, type ReactNode } from 'react'

import { SiteBrandingProvider, type SiteBranding } from '@/shared/layout/site-branding'
import { useSiteTheme, type SiteThemeColors } from '@/shared/theme/site-theme'

import type { CurrentSite } from './current-site'

/** Marks the style element that holds the site's custom CSS. */
export const customCssAttribute = 'data-site-css'

/** Whether `value` is an `https` or `http` address, the only kind an image may load from. */
function webAddress(value: string | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null
  }
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null
  } catch {
    return null
  }
}

/** What the shell shows of a site's branding: its logo, footer and navigation links. */
export function shellBranding(site: CurrentSite | null): SiteBranding | null {
  const branding = site?.branding
  if (branding === undefined) {
    return null
  }
  const logoUrl = webAddress(branding.logoUrl)
  return {
    logo: logoUrl === null ? null : { url: logoUrl, width: branding.logoWidthPixels ?? null, height: branding.logoHeightPixels ?? null },
    footerHtml: branding.footerHtml ?? null,
    navigationLinks: (branding.navigationLinks ?? []).map((link) => ({
      label: link.label,
      target: link.target,
      signedInOnly: link.audience === 'signedIn',
    })),
  }
}

/** Puts the site's favicon in place of the Skillable one while mounted. */
function useFavicon(address: string | null) {
  useLayoutEffect(() => {
    const icon = document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]')
    if (address === null || icon === null) {
      return undefined
    }
    const original = { href: icon.getAttribute('href'), type: icon.getAttribute('type') }
    icon.href = address
    // The page's own is an SVG; the site's is a PNG, JPEG or GIF, which the browser tells by itself.
    icon.removeAttribute('type')
    return () => {
      icon.setAttribute('href', original.href ?? '')
      if (original.type !== null) {
        icon.setAttribute('type', original.type)
      }
    }
  }, [address])
}

/** Adds the site's custom CSS to the page, last in `<head>` so it can override the theme, while mounted. */
function useCustomCss(css: string | null) {
  useLayoutEffect(() => {
    if (css === null || css.trim() === '') {
      return undefined
    }
    const style = document.createElement('style')
    style.setAttribute(customCssAttribute, '')
    // Text, never markup: nothing in it can close the element or add another.
    style.textContent = css
    document.head.append(style)
    return () => {
      style.remove()
    }
  }, [css])
}

/**
 * The site branding provider: applies the current site's theme colors, favicon and custom CSS to
 * the page, and gives the shell its logo, footer and navigation links, on every page, signed in
 * or out. Until the site loads, or for a site with no branding, the page stays as Skillable's.
 */
export function SiteBrandingApplied(props: { readonly site: CurrentSite | null; readonly children: ReactNode }) {
  const { site } = props
  const branding = site?.branding
  const colors = useMemo<SiteThemeColors | null>(
    () =>
      branding === undefined
        ? null
        : { primary: branding.primaryColor ?? null, secondary: branding.secondaryColor ?? null },
    [branding],
  )
  useSiteTheme(colors)
  useFavicon(webAddress(branding?.faviconUrl))
  useCustomCss(branding?.customCss ?? null)
  const shell = useMemo(() => shellBranding(site), [site])

  return <SiteBrandingProvider branding={shell}>{props.children}</SiteBrandingProvider>
}
